import React, { useState, useMemo, useEffect } from 'react';
import { router } from '@inertiajs/react';
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  TrendingUp, TrendingDown, Square, MoreVertical,
  Clock, Layers, Rocket, Play, Trash2, AlertCircle, Power, PowerOff, RotateCcw,
  LayoutGrid, List, ChevronRight, Plus, Archive, ArchiveRestore, X, Pause, CheckCircle2, Ban, Repeat, PlayCircle, Activity, Zap,
  ChevronUp, ChevronDown, Star
} from "lucide-react";
import { useAppSelector } from '@/store/hooks';
import { useLatestValid } from '../hooks/useLatestValid';

interface ActiveBotsProps {
  bots: any[];
  onBotClick: (botId: string) => void;
  onAddBot?: () => void;
  currentUser?: {
    email?: string;
    logoutPath?: string;
    is_active?: boolean;
    isActive?: boolean; // 호환성을 위해 추가
    is_admin?: boolean;
    maxBots?: number;
    currentBotCount?: number;
  };
  engine_stats?: { total_capacity: number };
  dca_enabled?: boolean;
  disable_all_testnet?: boolean;
  enabled_exchanges?: Record<string, boolean>;
}

import { calculateFirstEntryNotional, fmtDate, fmtPriceForSymbol, getExchangeBadgeStyle } from './dca/DcaUtils';

// Bot Item Component to handle its own hooks (Last Known PnL)
const BotItem = ({ bot, viewMode, onBotClick, isPrivate, actions }: any) => {
  const activeDetails = bot.active_cycles_details || [];
  const isActive = bot.is_active;
  const side = (bot.side || 'LONG').toUpperCase();
  const isBoth = side === 'BOTH';

  const anyPriceMissing = activeDetails.some((c: any) => c.unrealized_pnl === null || c.unrealized_pnl === undefined);
  const rawUnrealizedPnl = anyPriceMissing ? null : activeDetails.reduce((sum: number, c: any) => sum + Number(c.unrealized_pnl || 0), 0);
  const [unrealizedPnl, isStale] = useLatestValid(rawUnrealizedPnl);

  const totalPnl = Number(bot.total_pnl || 0);
  const totalMargin = activeDetails.reduce((sum: number, c: any) => sum + Number(c.total_cost || 0), 0);
  const totalFees = Number(bot.total_fees || 0);
  const leverage = Number(bot.leverage || 1);
  const hasCycle = activeDetails.length > 0;

  if (viewMode === 'compact') {
    return (
      <Card
        onClick={() => onBotClick(String(bot.id))}
        className={`transition-all border rounded-xl overflow-hidden group shadow-sm cursor-pointer ${bot.archived
          ? 'bg-muted/50 border-border/80'
          : 'bg-card hover:bg-muted/10 border-border/50'
          } ${isStale ? 'opacity-40' : ''}`}
      >
        <div className="flex items-center justify-between p-3 gap-4">
          <div className="flex items-center gap-2 min-w-[100px] sm:min-w-[140px]">
            <button
              onClick={(e) => actions.handleToggleFavorite(e, String(bot.id))}
              className="p-1 hover:bg-muted rounded-lg transition-colors shrink-0"
              title="즐겨찾기"
            >
              <Star className={`w-4 h-4 ${bot.is_favorite ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground/30 hover:text-amber-500'}`} />
            </button>
            <div className={`w-1.5 h-8 rounded-full shrink-0 ${isActive ? "bg-success shadow-[0_0_8px_rgba(16,185,129,0.4)]" : "bg-muted"}`} />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-black text-sm text-foreground truncate inline-block">{bot.symbol}</span>
                {isBoth ? (
                  <div className="flex gap-1">
                    {['long', 'short'].map(s => {
                      const cycle = activeDetails.find((c: any) => c.side === s);
                      const isSuspended = bot.suspended_sides?.includes(s);
                      const isPaused = bot.paused_buy_sides?.includes(s);
                      return (
                        <Badge key={s} variant="outline" className={`text-[9px] font-black h-4 px-1 border-none flex items-center gap-0.5 ${s === 'long' ? "bg-success/10 text-success" : "bg-danger/10 text-danger"} ${isSuspended ? 'opacity-50' : ''}`}>
                          {s === 'long' ? 'L' : 'S'}{cycle?.filled_steps || 0}
                          {isSuspended ? <span title="재진입 방지"><Square className="w-2.5 h-2.5 fill-current" /></span> : isPaused ? <span title="추매 금지"><Pause className="w-2.5 h-2.5 fill-current" /></span> : <span title="정상 운영"><Play className="w-2.5 h-2.5 fill-current" /></span>}
                        </Badge>
                      );
                    })}
                  </div>
                ) : (
                  (() => {
                    const isSuspended = bot.suspended_sides?.includes(side.toLowerCase());
                    const isPaused = bot.paused_buy_sides?.includes(side.toLowerCase());
                    return (
                      <Badge variant="outline" className={`text-[9px] font-black h-4 px-1 border-none flex items-center gap-0.5 ${side === 'LONG' ? "bg-success/10 text-success" : "bg-danger/10 text-danger"} ${isSuspended ? 'opacity-50' : ''}`}>
                        {side === 'LONG' ? 'L' : 'S'}{activeDetails[0]?.filled_steps || 0}
                        {isSuspended ? <span title="재진입 방지"><Square className="w-2.5 h-2.5 fill-current" /></span> : isPaused ? <span title="추매 금지"><Pause className="w-2.5 h-2.5 fill-current" /></span> : <span title="정상 운영"><Play className="w-2.5 h-2.5 fill-current" /></span>}
                      </Badge>
                    );
                  })()
                )}
                <span className="text-[10px] text-muted-foreground font-bold">
                  #{bot.id}
                  <span className={`ml-1 px-1 rounded text-[9px] uppercase ${getExchangeBadgeStyle(bot.exchange)} border-none`}>{bot.exchange || 'binance'}</span>
                  <span className={`ml-1 text-[9px] font-black ${bot.is_live ? 'text-primary' : 'text-amber-500'}`} title={bot.is_live ? 'Live Trading' : 'Paper Trading'}>
                    {bot.is_live ? 'L' : 'P'}
                  </span>
                  {bot.is_live && bot.use_testnet && (
                    <span className="ml-1 text-[9px] text-indigo-500 font-black" title="Testnet">T</span>
                  )}
                  <span className="ml-1 opacity-60">
                    {bot.cyclic ? <Repeat className="w-2.5 h-2.5 inline" /> : <PlayCircle className="w-2.5 h-2.5 inline" />}
                  </span>
                </span>
                {bot.source === 'auto_scanner' && (
                  <Badge variant="outline" className="ml-1 text-[9px] font-black h-4 px-1.5 border-none bg-purple-500/10 text-purple-600">
                    🤖 AUTO
                  </Badge>
                )}
                {bot.archived && <Archive className="w-3 h-3 text-muted-foreground/50 ml-1" />}
              </div>
              <p className="text-[10px] text-muted-foreground font-bold truncate opacity-60">{leverage}x · {bot.name}</p>
            </div>
          </div>

          <div className="flex-1 flex items-center justify-center gap-3 sm:gap-6 px-2 sm:px-4 min-w-0">
            <div className="text-center">
              <p className="text-[9px] font-bold text-muted-foreground uppercase leading-none mb-1">Unrealized</p>
              <p className={`text-sm font-black tabular-nums leading-none ${unrealizedPnl === null ? 'text-muted-foreground/30' : (unrealizedPnl >= 0 ? "text-success" : "text-danger")}`}>
                {isPrivate ? '****' : (unrealizedPnl === null ? '—' : `${unrealizedPnl >= 0 ? '+' : ''}${unrealizedPnl.toFixed(2)}`)}
              </p>
            </div>
            <div className="text-center hidden sm:block">
              <p className="text-[9px] font-bold text-muted-foreground uppercase leading-none mb-1">Total PNL / Fees</p>
              <p className={`text-sm font-black tabular-nums leading-none ${totalPnl >= 0 ? "text-success/70" : "text-danger/70"}`}>
                {isPrivate ? '****' : `${totalPnl >= 0 ? '+' : ''}${totalPnl.toFixed(2)}`}
                <span className="text-[10px] text-muted-foreground/40 font-bold ml-1">/ {isPrivate ? '****' : totalFees.toFixed(2)}</span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
              {bot.archived ? (
                <div className="p-1 hover:bg-primary/10 rounded-lg group/btn transition-colors cursor-pointer" onClick={(e) => actions.handleUnarchive(e, String(bot.id))}>
                  <ArchiveRestore className="w-4 h-4 text-primary/40 group-hover/btn:text-primary" />
                </div>
              ) : isActive ? (
                <div className="p-1 hover:bg-danger/10 rounded-lg group/btn transition-colors cursor-pointer" onClick={(e) => actions.handleStop(e, String(bot.id))}>
                  <Square className="w-4 h-4 text-danger/40 group-hover/btn:text-danger fill-danger/20" />
                </div>
              ) : (
                <div className={`p-1 rounded-lg group/btn transition-colors ${actions.isActiveUser ? 'hover:bg-success/10 cursor-pointer' : 'opacity-30 cursor-not-allowed'}`} onClick={(e) => actions.isActiveUser && actions.handleStart(e, String(bot.id))}>
                  <Play className={`w-4 h-4 ${actions.isActiveUser ? 'text-success/40 group-hover/btn:text-success fill-success/20' : 'text-muted-foreground'}`} />
                </div>
              )}
            </div>
            <ChevronRight className="w-4 h-4 hidden md:block text-muted-foreground/30 group-hover:text-primary transition-colors" />
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={`transition-all border-none rounded-2xl overflow-hidden group shadow-md ${bot.archived ? 'bg-muted/40 shadow-none' : 'bg-card hover:shadow-xl'
        } ${isStale ? 'opacity-40' : ''}`}
    >
      <CardHeader className={`p-5 border-t-4 transition-colors ${bot.archived ? 'bg-muted/30 border-muted' : (isBoth ? 'bg-primary/10 border-primary' : (side === 'LONG' ? 'bg-success/10 border-success' : 'bg-danger/10 border-danger'))
        }`}>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button
              onClick={(e) => actions.handleToggleFavorite(e, String(bot.id))}
              className="p-1 hover:bg-muted rounded-lg transition-colors shrink-0"
              title="즐겨찾기"
            >
              <Star className={`w-5 h-5 ${bot.is_favorite ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground/30 hover:text-amber-500'}`} />
            </button>
            {/* <div className={`p-2 rounded-xl h-10 w-10 flex items-center justify-center ${bot.archived ? 'bg-slate-100 text-slate-400' : (unrealizedPnl !== null && Number(unrealizedPnl) >= 0 ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger')
              }`}>
              {unrealizedPnl !== null && Number(unrealizedPnl) >= 0 ? <TrendingUp className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />}
            </div> */}
            <div>
              <h3 className="font-black text-foreground flex items-center gap-2">
                {bot.symbol}
                {bot.archived ? (
                  <Badge variant="outline" className="bg-muted text-muted-foreground border-none text-[9px] font-black h-4.5 px-1.5">ARCHIVED</Badge>
                ) : (
                  <Badge variant="outline" className={`text-[9px] font-black h-4.5 px-1.5 border-none ${isBoth ? 'bg-indigo-500/10 text-indigo-500' : (side === 'LONG' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger')
                    }`}>
                    {isBoth ? 'BOTH' : side}
                  </Badge>
                )}
                <span className="flex items-center gap-1">
                  <p className="text-[11px] text-muted-foreground font-bold uppercase tracking-tight">#{bot.id}</p>
                  {bot.archived && <Archive className="w-3 h-3 text-muted-foreground/60" />}
                </span>
              </h3>
              <p className="text-[10px] text-muted-foreground font-bold uppercase tracking-tight">
                {leverage}x · {bot.name}
              </p>
            </div>
          </div>

          <div className="flex flex-col items-end gap-1.5">
            {/* 첫 번째 줄: 모드 및 거래소 */}
            <div className="flex gap-1 items-center">
              {bot.source === 'auto_scanner' && (
                <Badge variant="outline" className="bg-purple-500/10 text-purple-600 border-purple-500/20 text-[9px] font-black h-4 px-1.5 leading-none uppercase">🤖 AUTO</Badge>
              )}
              <Badge variant="outline" className={`text-[9px] font-black h-4 px-1.5 leading-none uppercase ${bot.is_live ? 'bg-primary/10 text-primary border-primary/20' : 'bg-amber-500/10 text-amber-600 border-amber-600/20'}`}>{bot.is_live ? 'Live' : 'Paper'}</Badge>
              {bot.is_live && bot.use_testnet && (
                <Badge variant="outline" className="bg-indigo-500/10 text-indigo-500 border-indigo-500/20 text-[9px] font-black h-4 px-1.5 leading-none uppercase">Testnet</Badge>
              )}
              <Badge variant="secondary" className={`text-[9px] font-black h-4 px-1.5 leading-none uppercase ${getExchangeBadgeStyle(bot.exchange)}`}>
                {bot.exchange || 'binance'}
              </Badge>
            </div>

            {/* 두 번째 줄: 실행 상태 */}
            <div className="flex items-center">
              {bot.archived ? (
                <div className="flex flex-col items-end gap-0.5">
                  <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-tight">Archived</span>
                  {bot.archived_at && <span className="text-[8px] font-medium text-muted-foreground/70">{fmtDate(bot.archived_at)}</span>}
                </div>
              ) : bot.disabled ? (
                <Badge className="bg-danger/10 text-danger border-none text-[9px] font-black h-4 px-1.5">DISABLED</Badge>
              ) : isActive ? (
                <span className="flex items-center gap-1 text-[10px] font-bold text-success tracking-tight">
                  <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                  RUNNING
                </span>
              ) : (
                <span className="flex items-center gap-1 text-[10px] font-bold text-muted-foreground tracking-tight">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />
                  IDLE
                </span>
              )}

              <Badge variant="outline" className={`ml-2 text-[9px] font-black h-4 px-1 gap-1 leading-none uppercase ${bot.cyclic ? 'bg-primary/5 text-primary border-primary/20' : 'bg-muted text-muted-foreground border-border'}`}>
                {bot.cyclic ? <Repeat className="w-2 h-2" /> : <PlayCircle className="w-2 h-2" />}
                {bot.cyclic ? 'R' : '1'}
              </Badge>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-5 space-y-5">
        <div className="grid grid-cols-2 gap-4" onClick={() => onBotClick(String(bot.id))} style={{ cursor: 'pointer' }}>
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-muted-foreground uppercase text-[9px] font-bold">
              <Clock className="w-3 h-3" /> 시작 시간
            </div>
            <p className="text-[12px] font-black text-foreground">
              {(() => {
                const iso = bot.created_at;
                if (!iso) return 'WAITING...';
                const d = new Date(iso);
                const pad = (n: number) => n.toString().padStart(2, '0');
                return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
              })()}
            </p>
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-muted-foreground uppercase text-[9px] font-bold">
              <Layers className="w-3 h-3" /> 포지션 합계
            </div>
            <p className="text-[11px] font-black text-foreground uppercase">
              {activeDetails.length} Positions
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between items-end" onClick={() => onBotClick(String(bot.id))} style={{ cursor: 'pointer' }}>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <p className="text-[9px] font-bold text-muted-foreground uppercase">합산 미실현 손익</p>
                {totalMargin > 0 && unrealizedPnl !== null && (
                  <Badge variant="secondary" className={`text-[10px] h-4 px-1 font-black rounded border-none ${unrealizedPnl >= 0 ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'
                    }`}>
                    {isPrivate ? '****' : `${unrealizedPnl >= 0 ? '+' : ''}${(unrealizedPnl / totalMargin * 100).toFixed(2)}%`}
                  </Badge>
                )}
              </div>
              <p className={`text-xl font-black tracking-tight tabular-nums ${unrealizedPnl === null ? 'text-muted-foreground/30' : (unrealizedPnl >= 0 ? 'text-success' : 'text-danger')}`}>
                {isPrivate ? '****' : (unrealizedPnl === null ? '—' : `${unrealizedPnl >= 0 ? '+' : ''}${unrealizedPnl.toFixed(2)}`)}
                <span className="text-xs font-bold ml-1 opacity-40 uppercase">USDT</span>
              </p>
            </div>
            <div className="text-right space-y-1">
              <p className="text-[9px] font-bold text-muted-foreground uppercase">누계 손익 / 수수료</p>
              <p className={`text-sm font-black tabular-nums ${totalPnl >= 0 ? 'text-success/80' : 'text-danger/80'}`}>
                {isPrivate ? '****' : `${totalPnl >= 0 ? '+' : ''}${totalPnl.toFixed(2)}`} <span className="text-[10px] font-bold opacity-30">USDT</span>
              </p>
              <p className="text-[10px] font-bold text-muted-foreground/50 tabular-nums">
                Fees: {isPrivate ? '****' : totalFees.toFixed(2)} USDT
              </p>
            </div>
          </div>

          {/* 슈퍼 본전 탈출 모드 표시 */}
          {bot.status_message?.includes('슈퍼 본전') && (
            <div className="mb-3 p-2 bg-danger/10 border border-danger/20 rounded-xl flex items-center gap-2 animate-pulse shadow-sm">
              <Zap className="w-3.5 h-3.5 text-danger fill-danger/20" />
              <p className="text-[10px] font-black text-danger uppercase tracking-tight">슈퍼 본전 탈출 가동 중</p>
            </div>
          )}

          {/* 포지션별 진행도 (양방향 대응) */}
          <div className="space-y-2 pt-1">
            {/* 에러 메시지 표시 (🚫로 시작하는 경우) */}
            {bot.status_message?.startsWith('🚫') && (
              <div className="mb-3 p-2.5 bg-danger/10 border border-danger/20 rounded-xl flex items-start gap-2 shadow-sm animate-in fade-in zoom-in duration-300 relative group/err">
                <AlertCircle className="w-3.5 h-3.5 text-danger mt-0.5 shrink-0" />
                <p className="text-[10px] font-black text-danger leading-tight uppercase tracking-tight pr-5">{bot.status_message}</p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const target = e.currentTarget.parentElement;
                    if (target) target.style.display = 'none';
                  }}
                  className="absolute right-2 top-2 p-0.5 rounded-full hover:bg-danger/20 text-danger transition-colors opacity-0 group-hover/err:opacity-100"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            {activeDetails.map((c: any) => {
              const isSuspended = bot.suspended_sides?.includes(c.side);
              const isPaused = bot.paused_buy_sides?.includes(c.side);
              const displayMaxScaling = c.target || bot.max_scaling;
              return (
                <div key={c.id} className="space-y-1">
                  <div className="flex justify-between text-[9px] uppercase font-bold items-center">
                    <span className={c.side === 'long' ? 'text-success' : 'text-danger'}>{c.side} Scaling</span>
                    <span className="text-slate-500 flex items-center gap-1">
                      {c.filled_steps} / {displayMaxScaling}
                      {isSuspended ? (
                        <span className="flex items-center gap-0.5 text-danger" title="재진입 방지"><Square className="w-3 h-3 fill-current" /></span>
                      ) : isPaused ? (
                        <span className="flex items-center gap-0.5 text-amber-500" title="추매 금지"><Pause className="w-3 h-3 fill-current" /></span>
                      ) : (
                        <span className="flex items-center gap-0.5 text-success" title="정상 운영"><Play className="w-3 h-3 fill-current" /></span>
                      )}
                    </span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-700 rounded-full ${c.side === 'long' ? 'bg-success' : 'bg-danger'} ${isSuspended ? 'opacity-50 bg-danger' : isPaused ? 'bg-amber-500' : ''}`}
                      style={{ width: `${(c.filled_steps / displayMaxScaling) * 100}%` }}
                    />
                  </div>
                </div>
              )
            })}
            {!hasCycle && (
              <div className="h-1.5 bg-muted/50 rounded-full border border-dashed border-border" />
            )}
          </div>
        </div>

        <div className="flex gap-2 pt-2 border-t border-border">
          {bot.archived ? (
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-9 rounded-xl border-primary/20 text-primary font-bold hover:bg-primary/10"
              onClick={(e) => actions.handleUnarchive(e, String(bot.id))}
            >
              <ArchiveRestore className="w-3.5 h-3.5 mr-2" /> 복구 (Restore)
            </Button>
          ) : isActive ? (
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-9 rounded-xl border-danger/20 text-danger font-bold hover:bg-danger/10"
              onClick={(e) => actions.handleStop(e, String(bot.id))}
            >
              <Square className="w-3.5 h-3.5 fill-danger mr-2 border-none" /> 정지
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="flex-1 h-9 rounded-xl border-success/20 text-success font-bold hover:bg-success/10"
              onClick={(e) => actions.isActiveUser && actions.handleStart(e, String(bot.id))}
              disabled={!actions.isActiveUser}
            >
              <Play className="w-3.5 h-3.5 mr-2" /> 가동
            </Button>
          )}
          <Button
            size="sm"
            className="flex-1 h-9 rounded-xl bg-primary text-primary-foreground font-black hover:opacity-90 transition-opacity"
            onClick={() => onBotClick(String(bot.id))}
          >
            상세보기
          </Button>
          {!isActive && (
            <div className="flex gap-1">
              {!bot.archived && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-9 w-9 rounded-xl p-0 text-muted-foreground hover:text-primary hover:bg-primary/10"
                  onClick={(e) => actions.handleArchive(e, String(bot.id))}
                  title="보관하기"
                >
                  <Archive className="w-4 h-4" />
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="h-9 w-9 rounded-xl p-0 text-muted-foreground hover:text-danger hover:bg-danger/10"
                onClick={(e) => actions.handleDelete(e, String(bot.id))}
                title="영구 삭제"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export const ActiveBots: React.FC<ActiveBotsProps> = ({ bots: incomingBots, onBotClick, onAddBot, currentUser, engine_stats, dca_enabled = true, disable_all_testnet, enabled_exchanges }) => {
  const isPrivate = useAppSelector((state) => state.ui.isPrivate);
  const [bots, setBots] = useState(incomingBots);
  const [cooldowns, setCooldowns] = useState<any[]>([]);

  useEffect(() => {
    const fetchCooldowns = async () => {
      try {
        const res = await fetch('/dca/cooldown_status');
        const data = await res.json();
        if (data.ok) {
          setCooldowns(data.cooldowns || []);
        }
      } catch (err) {
        console.error('Failed to fetch cooldown status:', err);
      }
    };

    fetchCooldowns();
    const interval = setInterval(fetchCooldowns, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (cooldowns.length === 0) return;
    const timer = setInterval(() => {
      setCooldowns(prev =>
        prev
          .map(c => ({ ...c, remaining_seconds: c.remaining_seconds - 1 }))
          .filter(c => c.remaining_seconds > 0)
      );
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldowns.length]);

  const formatDuration = (seconds: number) => {
    if (seconds <= 0) return '00:00';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Inertia 프롭 변화 감지 및 동기화
  useEffect(() => {
    setBots(incomingBots);
  }, [incomingBots]);

  const handleStop = (e: React.MouseEvent, botId: string) => {
    e.stopPropagation();
    if (confirm('봇을 정지하시겠습니까?')) {
      router.post(`/dca/${botId}/stop`, {}, { preserveScroll: true });
    }
  };

  const handleStart = (e: React.MouseEvent, botId: string) => {
    e.stopPropagation();
    router.post(`/dca/${botId}/start`, {}, { preserveScroll: true });
  };

  const handleDelete = (e: React.MouseEvent, botId: string) => {
    e.stopPropagation();
    if (confirm('봇을 삭제하시겠습니까? 모든 이력이 삭제됩니다.')) {
      router.delete(`/dca/${botId}`, { preserveScroll: true });
    }
  };

  const handleReset = (e: React.MouseEvent, botId: string) => {
    e.stopPropagation();
    if (confirm('포지션을 초기화하시겠습니까? 모든 미체결/체결 데이터가 삭제됩니다.')) {
      router.post(`/dca/${botId}/reset`, {}, { preserveScroll: true });
    }
  };

  const handleToggleDisabled = (e: React.MouseEvent, botId: string) => {
    e.stopPropagation();
    router.post(`/dca/${botId}/toggle_disabled`, {}, { preserveScroll: true });
  };

  const handleArchive = (e: React.MouseEvent, botId: string) => {
    e.stopPropagation();
    if (confirm('이 봇을 보관하시겠습니까? 목록에서 숨겨집니다.')) {
      router.post(`/dca/${botId}/archive`, {}, {
        preserveScroll: true,
        onSuccess: () => {
          router.reload({ only: ['initial_bots'] });
        }
      });
    }
  };

  const handleUnarchive = (e: React.MouseEvent, botId: string) => {
    e.stopPropagation();
    router.post(`/dca/${botId}/unarchive`, {}, {
      preserveScroll: true,
      onSuccess: () => {
        router.reload({ only: ['initial_bots'] });
      }
    });
  };

  const handleToggleFavorite = (e: React.MouseEvent, botId: string) => {
    e.stopPropagation();
    router.post(`/dca/${botId}/toggle_favorite`, {}, {
      preserveScroll: true,
      onSuccess: () => {
        router.reload({ only: ['initial_bots'] });
      }
    });
  };


  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('bot_sort_by') || 'status';
  });
  const [filterMode, setFilterMode] = useState<'all' | 'mainnet' | 'testnet' | 'paper'>(() => {
    const saved = localStorage.getItem('bot_filter_mode');
    if (saved === 'real') return 'mainnet'; // 이전 버전 호환성
    return (saved as any) || 'all';
  });
  const [exchangeFilter, setExchangeFilter] = useState<'all' | string>(() => {
    return localStorage.getItem('bot_exchange_filter') || 'all';
  });

  const activeExchanges = useMemo(() => {
    if (!enabled_exchanges) return [];
    return Object.entries(enabled_exchanges)
      .filter(([_, enabled]) => enabled)
      .map(([name, _]) => name);
  }, [enabled_exchanges]);

  const handleSortByChange = (val: string) => {
    setSortBy(val);
    localStorage.setItem('bot_sort_by', val);
  };

  const handleFilterModeChange = (mode: 'all' | 'mainnet' | 'testnet' | 'paper') => {
    setFilterMode(mode);
    localStorage.setItem('bot_filter_mode', mode);
  };

  const handleExchangeFilterChange = (val: string) => {
    setExchangeFilter(val);
    localStorage.setItem('bot_exchange_filter', val);
  };
  const [viewMode, setViewMode] = useState<'grid' | 'compact'>(() => {
    return (localStorage.getItem('bot_view_mode') as 'grid' | 'compact') || 'grid';
  });

  const handleViewModeChange = (mode: 'grid' | 'compact') => {
    setViewMode(mode);
    localStorage.setItem('bot_view_mode', mode);
  };

  const [showArchived, setShowArchived] = useState(() => {
    return localStorage.getItem('bot_show_archived') === 'true';
  });

  const handleShowArchivedChange = (show: boolean) => {
    setShowArchived(show);
    localStorage.setItem('bot_show_archived', String(show));
  };

  const [showActiveOnly, setShowActiveOnly] = useState(() => {
    return localStorage.getItem('bot_show_active_only') === 'true';
  });

  const handleShowActiveOnlyChange = (show: boolean) => {
    setShowActiveOnly(show);
    localStorage.setItem('bot_show_active_only', String(show));
  };

  const [showFavoritesOnly, setShowFavoritesOnly] = useState(() => {
    return localStorage.getItem('bot_show_favorites_only') === 'true';
  });

  const handleShowFavoritesOnlyChange = (show: boolean) => {
    setShowFavoritesOnly(show);
    localStorage.setItem('bot_show_favorites_only', String(show));
  };

  const [isCooldownCollapsed, setIsCooldownCollapsed] = useState(() => {
    return localStorage.getItem('cooldown_collapsed') === 'true';
  });

  const handleCooldownCollapsedChange = (collapsed: boolean) => {
    setIsCooldownCollapsed(collapsed);
    localStorage.setItem('cooldown_collapsed', String(collapsed));
  };

  const sortedBots = useMemo(() => {
    let filtered = [...bots];

    // 아카이브 필터링 (기본적으로 숨김)
    if (!showArchived) {
      filtered = filtered.filter(bot => bot.archived !== true && bot.archived !== 1);
    }

    if (filterMode === 'mainnet') filtered = filtered.filter(bot => bot.is_live && !bot.use_testnet);
    if (filterMode === 'testnet') filtered = filtered.filter(bot => bot.is_live && bot.use_testnet);
    if (filterMode === 'paper') filtered = filtered.filter(bot => !bot.is_live);

    if (exchangeFilter !== 'all') {
      filtered = filtered.filter(bot => (bot.exchange || 'binance').toLowerCase() === exchangeFilter.toLowerCase());
    }

    if (showActiveOnly) {
      filtered = filtered.filter(bot => bot.is_active === true);
    }

    if (showFavoritesOnly) {
      filtered = filtered.filter(bot => !!bot.is_favorite);
    }

    return filtered.sort((a, b) => {
      const aFav = !!a.is_favorite;
      const bFav = !!b.is_favorite;
      if (aFav && !bFav) return -1;
      if (!aFav && bFav) return 1;

      if (sortBy === 'status') {
        // Use active status as primary sort key
        if (a.is_active && !b.is_active) return -1;
        if (!a.is_active && b.is_active) return 1;

        // Use is_live status as secondary sort key (Live first)
        if (a.is_live && !b.is_live) return -1;
        if (!a.is_live && b.is_live) return 1;

        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
      if (sortBy === 'created_desc') return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      if (sortBy === 'created_asc') return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      if (sortBy === 'name_asc') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'name_desc') return (b.name || '').localeCompare(a.name || '');
      return 0;
    });
  }, [bots, sortBy, filterMode, showArchived, exchangeFilter, showActiveOnly, showFavoritesOnly]);

  const actions = {
    handleStop,
    handleStart,
    handleDelete,
    handleReset,
    handleToggleDisabled,
    handleArchive,
    handleUnarchive,
    handleToggleFavorite,
    isActiveUser: currentUser?.is_active !== false && dca_enabled,
    canCreateBot: dca_enabled
  };

  return (
    <div className="space-y-4">
      {/* 쿨다운 / 서킷 브레이커 현황판 */}
      {cooldowns.length > 0 && (
        isCooldownCollapsed ? (
          <div 
            onClick={() => handleCooldownCollapsedChange(false)}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-danger/10 border border-danger/20 text-danger rounded-full text-xs font-black cursor-pointer hover:bg-danger/20 transition-all select-none animate-in fade-in"
          >
            <Ban className="w-3.5 h-3.5 text-danger animate-pulse shrink-0" />
            <span>차단 중 ({cooldowns.length})</span>
            <ChevronDown className="w-3.5 h-3.5 text-danger" />
          </div>
        ) : (
          <Card className="border-danger/20 bg-danger/5 shadow-md rounded-2xl overflow-hidden animate-in fade-in duration-300">
            <CardHeader
              className="py-3 px-5 bg-danger/10 border-b border-danger/10 flex flex-row items-center justify-between cursor-pointer hover:bg-danger/20 transition-colors select-none"
              onClick={() => handleCooldownCollapsedChange(true)}
            >
              <div className="flex items-center gap-2">
                <Ban className="w-4 h-4 text-danger animate-pulse" />
                <h3 className="font-black text-xs text-danger uppercase tracking-wider">실시간 진입 차단 현황 (전역 Cooldown & Circuit Breaker)</h3>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-danger/20 text-danger border-none text-[9px] font-black h-5">
                  {cooldowns.length}개 심볼
                </Badge>
                <ChevronUp className="w-4 h-4 text-danger" />
              </div>
            </CardHeader>
            <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 animate-in slide-in-from-top-2 duration-200">
              {cooldowns.map((c) => (
                <div key={`${c.symbol}:${c.type}`} className="flex items-center justify-between p-2.5 bg-card/65 border border-border/40 rounded-xl shadow-sm">
                  <div className="min-w-0 flex-1 pr-2">
                    <div className="flex items-center gap-1.5">
                      <span className="font-black text-xs text-foreground">{c.symbol}</span>
                      <Badge variant="outline" className={`text-[8px] font-black h-4 px-1 border-none ${c.type === 'circuit_breaker' ? 'bg-red-500/10 text-red-500' : 'bg-amber-500/10 text-amber-500'}`}>
                        {c.type === 'circuit_breaker' ? 'BREAKER' : 'COOLDOWN'}
                      </Badge>
                    </div>
                    <p className="text-[10px] font-bold text-muted-foreground mt-0.5 truncate leading-tight">{c.message}</p>
                  </div>
                  <div className="flex items-center gap-1 bg-muted/60 px-2 py-1 rounded-lg text-foreground font-black text-xs tabular-nums shrink-0">
                    <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                    {formatDuration(c.remaining_seconds)}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )
      )}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
        <h2 className="text-lg font-black text-foreground flex items-center gap-2 uppercase tracking-tight">
          <Rocket className="w-5 h-5 text-primary" /> 가동 중인 전략 ({currentUser?.currentBotCount || 0} / {currentUser?.maxBots || 10})
        </h2>
        <div id='filter_group' className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          {/* 필터 버튼 그룹 */}
          <div className="flex items-center gap-1 bg-muted/30 p-1 rounded-xl border border-border/50 max-w-full overflow-x-auto no-scrollbar">
            <Button
              variant={filterMode === 'all' ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-lg font-bold text-[10px] h-7 px-3 shrink-0 ${filterMode === 'all' ? 'shadow-sm' : 'text-muted-foreground'}`}
              onClick={() => handleFilterModeChange('all')}
            >
              전체
            </Button>
            <Button
              variant={filterMode === 'mainnet' ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-lg font-bold text-[10px] h-7 px-3 shrink-0 ${filterMode === 'mainnet' ? 'shadow-sm' : 'text-muted-foreground'}`}
              onClick={() => handleFilterModeChange('mainnet')}
            >
              실전
            </Button>
            {!disable_all_testnet && (
              <Button
                variant={filterMode === 'testnet' ? 'default' : 'ghost'}
                size="sm"
                className={`rounded-lg font-bold text-[10px] h-7 px-3 shrink-0 ${filterMode === 'testnet' ? 'shadow-sm' : 'text-muted-foreground'}`}
                onClick={() => handleFilterModeChange('testnet')}
              >
                테스트넷
              </Button>
            )}
            <Button
              variant={filterMode === 'paper' ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-lg font-bold text-[10px] h-7 px-3 shrink-0 ${filterMode === 'paper' ? 'shadow-sm' : 'text-muted-foreground'}`}
              onClick={() => handleFilterModeChange('paper')}
            >
              시뮬레이션
            </Button>
            <Button
              variant={showActiveOnly ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-lg font-bold text-[10px] h-7 px-3 shrink-0 ${showActiveOnly ? 'shadow-sm' : 'text-muted-foreground'}`}
              onClick={() => handleShowActiveOnlyChange(!showActiveOnly)}
            >
              가동 中
            </Button>
            <Button
              variant={showFavoritesOnly ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-lg font-bold text-[10px] h-7 px-3 shrink-0 flex items-center gap-1 ${showFavoritesOnly ? 'shadow-sm text-amber-500' : 'text-muted-foreground'}`}
              onClick={() => handleShowFavoritesOnlyChange(!showFavoritesOnly)}
            >
              <Star className={`w-3 h-3 ${showFavoritesOnly ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground'}`} />              
            </Button>
          </div>

          {/* 거래소 필터 (활성화된 거래소가 2개 이상일 때만 노출) */}
          {activeExchanges.length >= 2 && (
            <div className="flex items-center gap-1.5 bg-muted/30 p-1 rounded-xl border border-border/50">
              <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest pl-2">거래소</span>
              <Select value={exchangeFilter} onValueChange={handleExchangeFilterChange}>
                <SelectTrigger className="w-full sm:w-[120px] h-7 text-[10px] font-bold bg-card border-border rounded-lg shadow-sm focus:ring-1 focus:ring-primary">
                  <SelectValue placeholder="거래소" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">전체 거래소</SelectItem>
                  {activeExchanges.map(ex => (
                    <SelectItem key={ex} value={ex} className="uppercase">{ex}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center gap-1.5 bg-muted/30 p-1 rounded-xl border border-border/50">
            <Button
              variant={showArchived ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-lg font-bold text-[10px] h-7 px-3 ${showArchived ? 'shadow-sm' : 'text-muted-foreground'}`}
              onClick={() => handleShowArchivedChange(!showArchived)}
            >
              <Archive className="w-3.5 h-3.5 mr-1" />
              보관된 봇 {showArchived ? '숨기기' : '보기'}
            </Button>
          </div>

          {/* 보기 모드 토글 */}
          <div className="flex items-center gap-1 bg-muted/30 p-1 rounded-xl border border-border/50">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-lg w-8 h-7 p-0 ${viewMode === 'grid' ? 'shadow-sm' : 'text-muted-foreground'}`}
              onClick={() => handleViewModeChange('grid')}
            >
              <LayoutGrid className="w-3.5 h-3.5" />
            </Button>
            <Button
              variant={viewMode === 'compact' ? 'default' : 'ghost'}
              size="sm"
              className={`rounded-lg w-8 h-7 p-0 ${viewMode === 'compact' ? 'shadow-sm' : 'text-muted-foreground'}`}
              onClick={() => handleViewModeChange('compact')}
            >
              <List className="w-3.5 h-3.5" />
            </Button>
          </div>

          {/* 정렬 셀렉트 박스 */}
          <div className="flex items-center gap-2 bg-muted/30 p-1 rounded-xl border border-border/50">
            <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest pl-2">Sort</span>
            <Select value={sortBy} onValueChange={handleSortByChange}>
              <SelectTrigger className="w-full sm:w-[120px] h-8 text-[11px] font-bold bg-card border-border rounded-lg shadow-sm focus:ring-1 focus:ring-primary">
                <SelectValue placeholder="정렬 방식" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="status">상태별</SelectItem>
                <SelectItem value="name_asc">이름순 ↑</SelectItem>
                <SelectItem value="name_desc">이름역순 ↓</SelectItem>
                <SelectItem value="created_desc">생성순 ↑</SelectItem>
                <SelectItem value="created_asc">생성순 ↓</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-3"}>
        {sortedBots.map((bot) => (
          <BotItem
            key={bot.id}
            bot={bot}
            viewMode={viewMode}
            onBotClick={onBotClick}
            isPrivate={isPrivate}
            actions={actions}
          />
        ))}

        {/* 추가 카드 */}
        <Card
          onClick={() => currentUser?.is_active !== false && onAddBot?.()}
          className={`bg-muted/50 border-dashed border-2 border-border flex flex-col items-center justify-center rounded-2xl group transition-colors ${currentUser?.is_active !== false ? 'hover:border-primary/50 cursor-pointer' : 'opacity-50 cursor-not-allowed'} ${viewMode === 'grid' ? "p-8 min-h-[300px]" : "p-3 min-h-[50px]"}`}
        >
          {viewMode === 'grid' ? (
            <>
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                {actions.canCreateBot ? (
                  <Rocket className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-danger" />
                )}
              </div>
              <h3 className="font-bold text-foreground">새로운 봇 만들기</h3>
              <p className="text-xs text-muted-foreground text-center mt-2 max-w-[200px]">
                DCA 전략을 설정하고<br />최적의 트레이딩을 시작하세요.
              </p>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <>
                <Plus className="w-4 h-4 text-muted-foreground group-hover:text-primary" />
                <span className="text-sm font-bold text-muted-foreground group-hover:text-primary">새로운 봇 추가</span>
              </>
            </div>
          )}
        </Card>
      </div>

      {/* ── 전체 봇 제어 패널 ───────────────────────────────────────────────────────── */}
      {bots.length > 0 && (
        <div className="md:col-span-2 lg:col-span-3 mt-4 pt-8 pb-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-border">
          <div className="text-center sm:text-left">
            <h4 className="text-xs font-black text-foreground uppercase tracking-tight flex items-center justify-center sm:justify-start gap-1.5">
              <Rocket className="w-3.5 h-3.5 text-primary" /> DCA 봇 군단 통합 제어
              {engine_stats && engine_stats.total_capacity === 0 && (
                <Badge variant="destructive" className="ml-2 bg-danger/10 text-danger border-none text-[9px] animate-pulse">엔진 중지됨</Badge>
              )}
            </h4>
            <p className="text-[10px] text-muted-foreground font-bold uppercase mt-0.5">
              {engine_stats && engine_stats.total_capacity === 0
                ? "엔진이 중지되어 가동 명령을 처리할 수 없습니다"
                : "비활성화 된 봇은 시작 대상에서 제외됩니다"}
            </p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              onClick={() => confirm('활성된 모든 봇을 가동하시겠습니까?') && router.post('/dca/start_all')}
              className="flex-1 sm:flex-none bg-success hover:bg-success/80 text-white font-black text-xs px-6 rounded-xl shadow-lg h-9 transition-all active:scale-95"
              disabled={currentUser?.is_active === false}
            >
              <Play className="w-3.5 h-3.5 mr-2 fill-white" /> 전체 가동
            </Button>
            <Button
              onClick={() => confirm('모든 봇의 가동을 정지하시겠습니까?') && router.post('/dca/stop_all')}
              variant="outline"
              className="flex-1 sm:flex-none border-danger/20 text-danger hover:bg-danger/10 font-black text-xs px-6 rounded-xl h-9 transition-all active:scale-95"
            >
              <Square className="w-3.5 h-3.5 mr-2 fill-danger border-none" /> 전체 정지
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
